import { zodResolver } from '@hookform/resolvers/zod'
import { Boxes, Headset, Loader2, Mail, Wrench } from 'lucide-react'
import type { ReactNode } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { Field, FormSection, ReadonlyValue } from '@/components/common/FormBits'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { NumberInput } from '@/components/ui/number-input'
import { FormSelect } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useCatalog } from '@/features/projects/catalogApi'
import { isApiError } from '@/lib/api/errors'
import { useCustomerModules, useCustomers, useSaveModule, useSaveRequest, useSaveWork, useSendEmail } from '../../api'
import { MODULE_STATUSES, PRIORITIES, REQUEST_STATUSES, WORK_KINDS, WORK_STATUSES, type CustomerModule, type SupportRequest, type WorkRecord } from '../../types'
import { fmtMoney } from '../../../shared/format'
import { toastError, useCrm } from '../../ui'
import { useContacts } from '@/features/contacts/api'

const today = () => new Date().toISOString().slice(0, 10)

export function Shell({ icon, title, hint, pending, onDone, onSubmit, submitLabel, children }: { icon: ReactNode; title: string; hint: string; pending: boolean; onDone: () => void; onSubmit: (e: React.FormEvent<HTMLFormElement>) => void; submitLabel?: string; children: ReactNode }) {
  const { t } = useTranslation()
  return (
    <Modal
      open
      size="lg"
      onOpenChange={(v) => !v && onDone()}
      dismissible={!pending}
      icon={icon}
      title={title}
      description={hint}
      onSubmit={onSubmit}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={pending}>
            {pending ? <Loader2 className="animate-spin" /> : null}
            {submitLabel ?? t('common.save')}
          </Button>
        </>
      }
    >
      {children}
    </Modal>
  )
}

export function ModuleForm({ customerId, module, onDone }: { customerId: string; module: CustomerModule | null; onDone: () => void }) {
  const { t } = useTranslation()
  const save = useSaveModule()
  const catalog = useCatalog()
  const customer = useCustomers().data?.find((c) => c.id === customerId)
  const offered = (catalog.data ?? []).flatMap((m) => {
    const u = customer ? m.usage.find((x) => x.projectId === customer.projectId) : undefined
    return u && (m.active || m.id === module?.moduleId) ? [{ module: m, price: u.price }] : []
  })
  const schema = z.object({
    moduleId: z.string().min(1, t('crm.module.moduleRequired')),
    startDate: z.string().min(1, t('crm.form.dateRequired')),
    status: z.enum(MODULE_STATUSES),
    price: z.string().regex(/^\d+(\.\d{1,2})?$/, t('crm.form.amountInvalid')),
  })
  const form = useForm({ resolver: zodResolver(schema), defaultValues: { moduleId: module?.moduleId ?? '', startDate: module?.startDate ?? today(), status: module?.status ?? 'planned', price: module?.price ?? '' } })
  const { errors } = form.formState
  const moduleId = useWatch({ control: form.control, name: 'moduleId' })
  const selected = offered.find((o) => o.module.id === moduleId)
  const onSubmit = form.handleSubmit(async (v) => {
    try {
      await save.mutateAsync({ id: module?.id, input: { ...v, customerId } })
      toast.success(t('common.saved'))
      onDone()
    } catch (e) {
      if (isApiError(e) && (e.code === 'CUSTOMER_MODULE_EXISTS' || e.code === 'MODULE_NOT_IN_PRODUCT')) form.setError('moduleId', { message: t(`errors.${e.code}`) })
      else toastError(e)
    }
  })
  return (
    <Shell icon={<Boxes />} title={t(module ? 'crm.module.editTitle' : 'crm.module.createTitle')} hint={t('crm.module.hint')} pending={save.isPending} onDone={onDone} onSubmit={(e) => void onSubmit(e)}>
      <FormSection>
        <Field id="md-module" label={t('crm.module.name')} error={errors.moduleId?.message} hint={t('crm.module.fromCatalog')} full>
          <FormSelect
            id="md-module"
            control={form.control}
            name="moduleId"
            loading={catalog.isLoading}
            placeholder={t('crm.module.choose')}
            emptyText={t('crm.module.noneOffered')}
            onValueChange={(id) => {
              const o = offered.find((x) => x.module.id === id)
              if (o && (!module || !form.getValues('price'))) form.setValue('price', o.price, { shouldValidate: true })
            }}
            options={offered.map((o) => ({ value: o.module.id, label: o.module.name, description: `${o.module.code} · ${t(`crm.license.${o.module.pricing}`)} · ${fmtMoney(o.price)}` }))}
          />
        </Field>
        <Field id="md-lic" label={t('crm.module.license')}>
          <ReadonlyValue id="md-lic">{selected ? t(`crm.license.${selected.module.pricing}`) : '—'}</ReadonlyValue>
        </Field>
        <Field id="md-price" label={t('crm.module.price')} error={errors.price?.message}>
          <Controller control={form.control} name="price" render={({ field }) => <NumberInput id="md-price" value={field.value} onChange={field.onChange} onBlur={field.onBlur} minDecimalsOnBlur={2} suffix="₺" />} />
        </Field>
        <Field id="md-start" label={t('crm.module.start')}>
          <Controller control={form.control} name="startDate" render={({ field }) => <DateField id="md-start" value={field.value} onChange={field.onChange} clearable={false} />} />
        </Field>
        <Field id="md-status" label={t('crm.fields.status')}>
          <FormSelect id="md-status" control={form.control} name="status" options={MODULE_STATUSES.map((s) => ({ value: s, label: t(`crm.moduleStatus.${s}`) }))} />
        </Field>
      </FormSection>
    </Shell>
  )
}

export function WorkForm({ customerId, work, onDone }: { customerId: string; work: WorkRecord | null; onDone: () => void }) {
  const { t } = useTranslation()
  const save = useSaveWork()
  const { staff } = useCrm()
  const schema = z.object({
    kind: z.enum(WORK_KINDS),
    date: z.string().min(1, t('crm.form.dateRequired')),
    staffId: z.string().min(1, t('crm.form.staffRequired')),
    status: z.enum(WORK_STATUSES),
    note: z.string().trim().max(400),
  })
  const form = useForm({ resolver: zodResolver(schema), defaultValues: { kind: work?.kind ?? 'demo', date: work?.date ?? today(), staffId: work?.staffId ?? staff[0]?.id ?? '', status: work?.status ?? 'planned', note: work?.note ?? '' } })
  const { errors } = form.formState
  const onSubmit = form.handleSubmit(async (v) => {
    try {
      await save.mutateAsync({ id: work?.id, input: { ...v, customerId } })
      toast.success(t('common.saved'))
      onDone()
    } catch (e) {
      toastError(e)
    }
  })
  return (
    <Shell icon={<Wrench />} title={t(work ? 'crm.work.editTitle' : 'crm.work.createTitle')} hint={t('crm.work.hint')} pending={save.isPending} onDone={onDone} onSubmit={(e) => void onSubmit(e)}>
      <FormSection>
        <Field id="wk-kind" label={t('crm.work.kind')}>
          <FormSelect id="wk-kind" control={form.control} name="kind" options={WORK_KINDS.map((k) => ({ value: k, label: t(`crm.workKind.${k}`) }))} />
        </Field>
        <Field id="wk-date" label={t('crm.fields.date')} error={errors.date?.message}>
          <Controller control={form.control} name="date" render={({ field }) => <DateField id="wk-date" value={field.value} onChange={field.onChange} clearable={false} />} />
        </Field>
        <Field id="wk-staff" label={t('crm.fields.staff')} error={errors.staffId?.message}>
          <FormSelect id="wk-staff" control={form.control} name="staffId" options={staff.map((s) => ({ value: s.id, label: s.name, description: s.title }))} />
        </Field>
        <Field id="wk-status" label={t('crm.fields.status')}>
          <FormSelect id="wk-status" control={form.control} name="status" options={WORK_STATUSES.map((s) => ({ value: s, label: t(`crm.workStatus.${s}`) }))} />
        </Field>
        <Field id="wk-note" label={t('crm.fields.note')} full>
          <Textarea id="wk-note" rows={3} maxLength={400} {...form.register('note')} />
        </Field>
      </FormSection>
    </Shell>
  )
}

export function RequestForm({ customerId, request, onDone }: { customerId: string; request: SupportRequest | null; onDone: () => void }) {
  const { t } = useTranslation()
  const save = useSaveRequest()
  const { staff } = useCrm()
  const modules = [...new Set((useCustomerModules().data ?? []).filter((m) => m.customerId === customerId && m.status !== 'cancelled').map((m) => m.name))]
  const schema = z.object({
    title: z.string().trim().min(3, t('crm.request.titleRequired')).max(120),
    description: z.string().trim().max(1000),
    priority: z.enum(PRIORITIES),
    status: z.enum(REQUEST_STATUSES),
    assigneeId: z.string(),
    module: z.string(),
  })
  const form = useForm({ resolver: zodResolver(schema), defaultValues: { title: request?.title ?? '', description: request?.description ?? '', priority: request?.priority ?? 'medium', status: request?.status ?? 'open', assigneeId: request?.assigneeId ?? '', module: request?.module ?? '' } })
  const { errors } = form.formState
  const onSubmit = form.handleSubmit(async (v) => {
    try {
      await save.mutateAsync({ id: request?.id, input: { ...v, customerId, assigneeId: v.assigneeId || null } })
      toast.success(t('common.saved'))
      onDone()
    } catch (e) {
      toastError(e)
    }
  })
  return (
    <Shell icon={<Headset />} title={request ? t('crm.request.editTitle', { number: request.number }) : t('crm.request.createTitle')} hint={t('crm.request.hint')} pending={save.isPending} onDone={onDone} onSubmit={(e) => void onSubmit(e)}>
      <FormSection>
        <Field id="rq-title" label={t('crm.request.title')} error={errors.title?.message} full>
          <Input id="rq-title" {...form.register('title')} />
        </Field>
        <Field id="rq-pri" label={t('crm.request.priority')}>
          <FormSelect id="rq-pri" control={form.control} name="priority" options={PRIORITIES.map((p) => ({ value: p, label: t(`crm.priority.${p}`) }))} />
        </Field>
        <Field id="rq-status" label={t('crm.fields.status')}>
          <FormSelect id="rq-status" control={form.control} name="status" options={REQUEST_STATUSES.map((s) => ({ value: s, label: t(`crm.requestStatus.${s}`) }))} />
        </Field>
        <Field id="rq-module" label={t('crm.request.module')}>
          <FormSelect id="rq-module" control={form.control} name="module" options={[{ value: '', label: t('crm.request.general') }, ...modules.map((m) => ({ value: m, label: m }))]} />
        </Field>
        <Field id="rq-assignee" label={t('crm.request.assignee')}>
          <FormSelect id="rq-assignee" control={form.control} name="assigneeId" options={[{ value: '', label: t('crm.unassigned') }, ...staff.map((s) => ({ value: s.id, label: s.name, description: s.title }))]} />
        </Field>
        <Field id="rq-desc" label={t('crm.fields.description')} full>
          <Textarea id="rq-desc" rows={4} maxLength={1000} {...form.register('description')} />
        </Field>
      </FormSection>
    </Shell>
  )
}

/** E-posta gönderim kaydı; gerçek gönderim CP'nin posta servisi bağlanınca yapılır. */
export function EmailForm({ customerId, to, onDone }: { customerId: string; to: string; onDone: () => void }) {
  const { t } = useTranslation()
  const send = useSendEmail()
  const contacts = useContacts(customerId)
  const suggestions = [...new Set([to, ...(contacts.data ?? []).map((c) => c.email)].filter(Boolean))]
  const schema = z.object({
    to: z.email(t('crm.form.emailInvalid')),
    subject: z.string().trim().min(2, t('crm.email.subjectRequired')).max(160),
    body: z.string().trim().min(2, t('crm.email.bodyRequired')).max(5000),
  })
  const form = useForm({ resolver: zodResolver(schema), defaultValues: { to, subject: '', body: '' } })
  const { errors } = form.formState
  const onSubmit = form.handleSubmit(async (v) => {
    try {
      await send.mutateAsync({ ...v, customerId })
      toast.success(t('crm.email.sent'))
      onDone()
    } catch (e) {
      toastError(e)
    }
  })
  return (
    <Shell icon={<Mail />} title={t('crm.email.title')} hint={t('crm.email.hint')} pending={send.isPending} onDone={onDone} onSubmit={(e) => void onSubmit(e)} submitLabel={t('crm.email.send')}>
      <FormSection>
        <Field id="em-to" label={t('crm.email.to')} error={errors.to?.message} full>
          <Input id="em-to" type="email" list="em-to-suggestions" {...form.register('to')} />
          <datalist id="em-to-suggestions">
            {suggestions.map((e) => (
              <option key={e} value={e} />
            ))}
          </datalist>
        </Field>
        <Field id="em-subject" label={t('crm.email.subject')} error={errors.subject?.message} full>
          <Input id="em-subject" {...form.register('subject')} />
        </Field>
        <Field id="em-body" label={t('crm.email.body')} error={errors.body?.message} full>
          <Textarea id="em-body" rows={8} maxLength={5000} {...form.register('body')} />
        </Field>
      </FormSection>
    </Shell>
  )
}
